import { Col, Row } from 'antd';
import { ColumnProps } from 'antd/es/table';
import dayjs from 'mixins/dayjsConfig.js';
import Button from 'mixins/ui/button/button';
import CmsTable from 'mixins/ui/cms-table';
import React, { useState } from 'react';
import { getChartsData, getPassedQuestionnaire } from './actions';
import { dateFormat, statuses } from '/react4xp/parts/questionnaire-ogd/questionnaire-ogd';
import { Survey, TQuestions } from './questionnaire-ogd.type';
import { localize } from '/react4xp/mixins/helpers';

const Results = ({
    surveys,
    translations,
    language,
    setStep,
    setSurvey,
    setAnswers,
    setStatsData,
    token,
    commonTranslations,
}: {
    surveys: Array<Survey>;
    translations: Record<string, string>;
    commonTranslations: Record<string, string>;
    language: string;
    setStep: (value: 'STATS' | 'MAIN' | 'QUESTIONS') => void;
    setSurvey: (record: any) => void;
    setAnswers: (survey: Array<TQuestions>) => void;
    setStatsData: (data: TQuestions[]) => void;
    token: string;
}) => {
    const [answersLoadingId, setAnswersLoadingId] = useState<number>();
    const [statsLoadingId, setStatsLoadingId] = useState<number>();
    const handleOpenQuestions = (record: any) => {
        setStep('QUESTIONS');
        setSurvey(record);
    };
    const handleOpenAnswers = (record: any) => {
        return () => {
            setAnswersLoadingId(record.id);
            getPassedQuestionnaire(record.id, token)
                .then((res) => {
                    setAnswers(res.data);
                    setStep('QUESTIONS');
                    setSurvey(record);
                })
                .finally(() => setAnswersLoadingId(undefined));
        };
    };
    const handleOpenStats = (record: any) => {
        setStatsLoadingId(record.id);
        getChartsData(record.id, language.slice(0, 2).toUpperCase(), token)
            .then((res) => {
                setStatsData(res.data);
                setStep('STATS');
                setSurvey(record);
            })
            .finally(() => setStatsLoadingId(undefined));
    };

    const columns: ColumnProps<Survey>[] = [
        {
            title: localize('oprosy-title', translations),
            dataIndex: 'theme',
            key: 'theme',
            align: 'center',
            width: '25%',
        },
        {
            title: localize('oprosy-survey-validity-period', translations),
            key: 'period',
            align: 'center',
            render: (value, row) => {
                return (
                    row.beginDate &&
                    row.endDate &&
                    dayjs.utc(row.beginDate).format(dateFormat) +
                        ' - ' +
                        dayjs.utc(row.endDate).format(dateFormat)
                );
            },
        },
        {
            title: localize('oprosy-status', translations),
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            render: (status: string) => statuses(translations).find((s) => s.value === status)?.label,
        },
        {
            title: localize('oprosy-questions-number', translations),
            key: 'questions',
            align: 'center',
            render: (text, record) => record.questions.length,
        },
        {
            title: localize('oprosy-open-survey', translations),
            key: 'action',
            width: '35%',
            render: (text, record, id) => {
                return (
                    <Row
                        gutter={[5, { md: 10 }]}
                        wrap={false}
                    >
                        {record.status === 'ACTIVE' && (
                            <Col>
                                <Button
                                    style={{ minWidth: '12.5rem' }}
                                    color="gold"
                                    key={record.id + id}
                                    onClick={() => handleOpenQuestions(record)}
                                >
                                    {localize('oprosy-button.take-survey', translations)}
                                </Button>
                            </Col>
                        )}
                        {(record.status === 'PASSED' || record.status === 'COMPLETED') && (
                            <>
                                <Col>
                                    <Button
                                        style={{ minWidth: '12.5rem' }}
                                        color="gold"
                                        key={record.id + id}
                                        loading={answersLoadingId === record.id}
                                        onClick={handleOpenAnswers(record)}
                                    >
                                        {localize('oprosy-button.view-responses', translations)}
                                    </Button>
                                </Col>
                                {record.publicAvailability && (
                                    <Col>
                                        <Button
                                            style={{ minWidth: '12.5rem' }}
                                            color="gold"
                                            key={record.id + id}
                                            loading={statsLoadingId === record.id}
                                            onClick={() => handleOpenStats(record)}
                                        >
                                            {localize('oprosy-button.view-results', translations)}
                                        </Button>
                                    </Col>
                                )}
                            </>
                        )}
                    </Row>
                );
            },
        },
    ];

    return (
        <CmsTable
            showResponseBlock={surveys.length > 0 ? false : true}
            dataSource={surveys}
            columns={columns}
            translations={commonTranslations}
            noResNumber={surveys.length > 0 ? true : false}
            type="default"
            size="small"
            scroll={{ x: 1200 }}
        />
    );
};

export default Results;
